import React, { useCallback } from 'react';

/**
 * Ant Design Table `components.header.cell` — adds a drag handle to resize column width.
 * Pass via column `onHeaderCell`: { width, minWidth?, onResize }.
 */
const ResizableTableHeaderCell = React.forwardRef((props, ref) => {
    const { children, onResize, width, minWidth = 48, className, style, ...rest } = props;

    if (typeof onResize !== 'function') {
        return (
            <th ref={ref} className={className} style={style} {...rest}>
                {children}
            </th>
        );
    }

    const onMouseDown = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startW = typeof width === 'number' ? width : minWidth;

            const onMove = (ev) => {
                const delta = ev.clientX - startX;
                onResize(Math.max(minWidth, Math.round(startW + delta)));
            };

            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };

            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [width, minWidth, onResize]
    );

    return (
        <th
            ref={ref}
            className={className}
            style={{ ...style, position: 'relative' }}
            {...rest}
        >
            {children}
            <span
                className="policylens-col-resize-handle"
                title="Drag to resize column"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize column"
                onMouseDown={onMouseDown}
                onClick={(e) => e.stopPropagation()}
            />
        </th>
    );
});

ResizableTableHeaderCell.displayName = 'ResizableTableHeaderCell';

export default ResizableTableHeaderCell;
